import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { Shield, Brain, DeviceMobile, TrendUp } from '@phosphor-icons/react';

const serviceImages = [
  '/images/service-bg-1.png',
  '/images/service-bg-2.jpeg',
  '/images/service-bg-3.png',
  '/images/service-bg-4.jpeg',
];

const icons = [Shield, Brain, DeviceMobile, TrendUp];

const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { t } = useLanguage();

  return (
    <section
      id="services"
      data-testid="services-section"
      ref={ref}
      className="py-24 md:py-32 lg:py-40 border-t border-zinc-900"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-gold/60 mb-8" data-testid="services-label">
            {t.services.label}
          </p>
          <h2 className="text-4xl md:text-6xl tracking-tight leading-none text-zinc-100" data-testid="services-title">
            {t.services.title}
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 md:gap-12">
          {t.services.items.map((service, index) => {
            const Icon = icons[index];
            return (
              <motion.div
                key={index}
                data-testid={`service-card-${index}`}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
                transition={{
                  duration: 1.2,
                  delay: index * 0.2,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="group relative bg-[#121212] border border-zinc-900 hover:border-gold/50 transition-all duration-500 overflow-hidden"
              >
                {/* Background Image */}
                <div className="absolute inset-0 grayscale-image">
                  <img
                    src={serviceImages[index]}
                    alt={service.title}
                    className="w-full h-full object-cover opacity-10"
                  />
                </div>

                {/* Content */}
                <div className="relative z-10 p-8 md:p-12">
                  <Icon size={48} weight="thin" className="text-gold mb-6" />
                  <h3 className="text-2xl md:text-3xl font-light text-zinc-100 mb-4">
                    {service.title}
                  </h3>
                  <p className="text-base leading-relaxed text-zinc-400">{service.description}</p>
                </div>

                {/* Hover effect - gold line */}
                <div className="absolute bottom-0 left-0 w-full h-[2px] bg-gold transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
